import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import FlowChartEditor from '../components/flow/FlowChartEditor';
import ConfirmDialog from '../components/shared/ConfirmDialog';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import Modal from '../components/shared/Modal';
import Button from '../components/shared/Button';
import { serializeScriptGraph, deserializeScriptGraph } from '../utils/graphSerialization';
import * as firestorePaths from '../utils/firestorePaths';
import { getArtifactProjectId } from '../utils/firestorePaths';

/**
 * FlowChartEditorPage - Standalone page for editing script flowcharts
 * 
 * This component loads templates directly from Firestore and saves changes
 * independently, eliminating the parent-child prop issues that caused
 * re-render regressions in the modal-based approach.
 */
const FlowChartEditorPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = window.location;
    
  // Parse templateId from URL since we're not using React Router's Route component
  const pathParts = location.pathname.split('/');
  const templateId = pathParts[pathParts.length - 1] || pathParts[pathParts.length - 2];
  
  // Get initial node to focus on (from ScriptBuilder "Back to Flowchart" button)
  const initialNodeId = searchParams.get('node');
  
  // Track if we need to reload (when coming back from ScriptBuilder)
  const refreshKey = searchParams.get('t') || '0';
    
  const { db, userId, userRole, userTeam, userEmail, isAdmin, isTeamLead, isAuthReady } = useAuth();
  const { refreshTemplates } = useData();
    
  const [graph, setGraph] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateMetadata, setTemplateMetadata] = useState(null); // NEW: Store complete template data
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Lead form state
  const [prospectForm, setProspectForm] = useState(null);
  const [showLeadFormModal, setShowLeadFormModal] = useState(false);
  const [teamSubscriptionPlan, setTeamSubscriptionPlan] = useState('starter');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showSaveBeforeLeaveConfirm, setShowSaveBeforeLeaveConfirm] = useState(false);
    
  const lastSavedGraphRef = useRef(null);
  const editorRef = useRef(null);
    
  // Use consistent appId for all environments - data is always in 'my-real-estate-assistant'
  const appId = getArtifactProjectId();
    
  // use shared graph serialization utils
    
  // Debug logging
  useEffect(() => {
    console.log('🔍 FlowChartEditorPage mount/update:', {
      templateId,
      hasDb: !!db,
      userTeam,
      userRole,
      userId,
      isAdmin,
      isTeamLead,
      isAuthReady
    });
  }, [templateId, db, userTeam, userRole, userId, isAdmin, isTeamLead, isAuthReady]);

  // Load team subscription plan
  useEffect(() => {
    if (!db || !userTeam) return;
    const loadTeamPlan = async () => {
      try {
        const teamRef = doc(db, firestorePaths.getTeamPath(appId, userTeam));
        const teamDoc = await getDoc(teamRef);
        if (teamDoc.exists()) {
          const teamData = teamDoc.data();
          if (teamData.subscription?.plan) {
            setTeamSubscriptionPlan(teamData.subscription.plan);
          }
        }
      } catch (err) {
        console.error('Error loading team subscription plan:', err);
      }
    };
    loadTeamPlan();
  }, [db, userTeam, appId]);
    
  // Load template from Firestore
  useEffect(() => {
    const loadTemplate = async () => {
      if (!isAuthReady) {
        console.log('⏳ Waiting for auth to be ready...', { isAuthReady });
        return;
      }
            
      if (!templateId) {
        console.log('⏳ Waiting for template ID from URL...', { templateId });
        return;
      }
            
      if (!db) {
        console.log('⏳ Waiting for database connection...', { db });
        return;
      }
            
      if (!userTeam) {
        console.log('⏳ Waiting for user team info...', { userTeam, userRole });
        return;
      }
            
      setIsLoading(true);
      setError(null);
            
      try {
        console.log(`📚 Loading template ${templateId} for team ${userTeam}`);
                
        // All team templates are editable - no read-only templates
        setIsReadOnly(false);
                
        // Try loading from team templates first
        let templateRef = doc(db, firestorePaths.getTemplatePath(appId, userTeam, templateId));
        let templateDoc = await getDoc(templateRef);
                
        // If not found in team templates, try global/system templates
        if (!templateDoc.exists()) {
          console.log('🌍 Template not in team collection, trying global system templates...');
          templateRef = doc(db, `${firestorePaths.getGlobalTemplatesPath(appId)}/${templateId}`);
          templateDoc = await getDoc(templateRef);
        }
                
        if (!templateDoc.exists()) {
          throw new Error(`Template ${templateId} not found`);
        }
                
        const templateData = templateDoc.data();
                
        // Store complete template data for preservation during save
        const completeTemplateData = {
          steps: templateData?.steps || templateData?.data?.steps || [],
          prospectForm: templateData?.prospectForm || templateData?.data?.prospectForm || {},
          name: templateData?.name || templateData?.metadata?.name || templateId,
          metadata: templateData?.metadata || {}
        };
                
                
        setTemplateMetadata(completeTemplateData);

        // Initialize prospect form state from template data
        setProspectForm(completeTemplateData.prospectForm || {
          title: 'Lead Data',
          fields: [
            { id: 'agentName', label: 'Agent Name', type: 'text', placeholder: 'Enter your name', required: true },
            { id: 'prospectName', label: 'Contact Name', type: 'text', placeholder: "Enter contact's full name", required: true },
            { id: 'company', label: 'Company', type: 'text', placeholder: 'Enter company name', required: false },
            { id: 'prospectPhone', label: 'Phone Number', type: 'tel', placeholder: 'Enter phone number', required: true }
          ]
        });
                
        // Extract graph from either location (backward compatibility)
        const rawGraph = templateData?.graph || templateData?.data?.scriptGraph;
                
        if (!rawGraph) {
          throw new Error('Template has no graph data');
        }
                
        const deserializedGraph = deserializeScriptGraph(rawGraph);
                
        console.log('✅ FlowChart initialized:', {
          nodeCount: Object.keys(deserializedGraph.nodes || {}).length,
          hasPositions: !!deserializedGraph.ui?.positions,
          fullGraph: deserializedGraph
        });
                
        setGraph(deserializedGraph);
        setTemplateName(completeTemplateData.name);
        lastSavedGraphRef.current = deserializedGraph;
                
      } catch (err) {
        console.error('❌ Error loading template:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
        
    loadTemplate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthReady, templateId, db, userTeam, appId, deserializeScriptGraph, refreshKey]);
    
  // Save to Firestore
  const saveToFirestore = useCallback(async (graphToSave) => {
    if (!templateId || !db || !userTeam || isReadOnly) {
      console.log('⏭️ Skipping save - missing requirements or read-only');
      return;
    }
        
    // Don't save if graph hasn't actually changed
    if (lastSavedGraphRef.current && 
            JSON.stringify(lastSavedGraphRef.current) === JSON.stringify(graphToSave)) {
      console.log('⏭️ Skipping save - no changes detected');
      return;
    }
        
    // 🚨 SAFETY CHECK: Never save empty graphs to prevent data loss
    const nodeCount = Object.keys(graphToSave?.nodes || {}).length;
    if (nodeCount === 0) {
      console.error('🚨 PREVENTED DATA LOSS: Refusing to save empty graph!', {
        templateId,
        graphToSave
      });
      setSaveStatus('❌ Cannot save empty template');
      setTimeout(() => setSaveStatus(''), 3000);
      return;
    }
        
    setIsSaving(true);
    setSaveStatus('Saving...');
        
    try {
      const templateRef = doc(db, firestorePaths.getTemplatePath(appId, userTeam, templateId));
      const serializedGraph = serializeScriptGraph(graphToSave);
            
      // Preserve complete template structure
      const dataToSave = {
        graph: serializedGraph,
        steps: templateMetadata?.steps || [],        // Preserve steps
        prospectForm: prospectForm || templateMetadata?.prospectForm || {}, // Use local state, fall back to metadata
        metadata: {
          name: templateName,
          lastModified: new Date(),
          modifiedBy: userEmail
        }
      };
            
      // Overwrite the document to ensure deletions persist and stale fields are removed
      await setDoc(templateRef, dataToSave);
            
      lastSavedGraphRef.current = graphToSave;
      setSaveStatus('Saved');
            
      // Clear status after 2 seconds
      setTimeout(() => setSaveStatus(''), 2000);
            
      // Refresh templates in DataContext so ScriptBuilder sees the latest changes
      if (refreshTemplates) {
        await refreshTemplates();
      }
            
    } catch (err) {
      console.error('❌ Error saving template:', err);
      setSaveStatus('Error saving');
      setError('Failed to save changes: ' + err.message);
            
      // Clear error status after 5 seconds
      setTimeout(() => setSaveStatus(''), 5000);
    } finally {
      setIsSaving(false);
    }
  }, [templateId, db, userTeam, appId, templateName, userEmail, isReadOnly, serializeScriptGraph, refreshTemplates, templateMetadata, prospectForm]);
    
  // Handle graph changes - just update state and mark as dirty
  const handleGraphChange = useCallback((newGraph) => {
    if (!newGraph || !newGraph.nodes || Object.keys(newGraph.nodes).length === 0) {
      console.warn('⚠️ Received empty or invalid graph from FlowChartEditor!');
      return;
    }
    setGraph(newGraph);
    setHasUnsavedChanges(true);
  }, []);
    
  // Manual save
  const handleManualSave = useCallback(async () => {
    // Prefer pulling live state from editor (ReactFlow is source of truth)
    let graphToPersist = graph;
    if (editorRef.current?.getCurrentGraph) {
      try {
        const liveGraph = editorRef.current.getCurrentGraph();
        graphToPersist = liveGraph;
      } catch (e) {
        console.warn('⚠️ Could not get live graph from editor, falling back to state.', e);
      }
    }
        
    if (graphToPersist) {
      try {
        await saveToFirestore(graphToPersist);
        setHasUnsavedChanges(false);
        setSaveStatus('✅ Changes saved');
        setTimeout(() => setSaveStatus(''), 3000);
      } catch (error) {
        console.error('❌ saveToFirestore failed:', error);
        setSaveStatus('❌ Save failed');
        setTimeout(() => setSaveStatus(''), 3000);
      }
    } else {
      console.warn('⚠️ handleManualSave: No graph to save!');
    }
  }, [graph, saveToFirestore]);
    
  // Discard changes and reload from last saved
  const handleDiscardChanges = useCallback(() => {
    setShowDiscardConfirm(true);
  }, []);

  const confirmDiscardChanges = useCallback(() => {
    setShowDiscardConfirm(false);
    setGraph(lastSavedGraphRef.current);
    setHasUnsavedChanges(false);
    setSaveStatus('⚠️ Changes discarded');
    setTimeout(() => setSaveStatus(''), 3000);
  }, []);
    
  // Handle back navigation
  const handleBack = useCallback(async () => {
    // Warn about unsaved changes
    if (hasUnsavedChanges && !isReadOnly) {
      setShowSaveBeforeLeaveConfirm(true);
      return;
    }
        
    navigate(`/my-scripts?tab=edit&template=${templateId}`);
  }, [hasUnsavedChanges, isReadOnly, navigate, templateId]);

  // Save and leave (from save-before-leave confirm dialog)
  const confirmSaveAndLeave = useCallback(async () => {
    setShowSaveBeforeLeaveConfirm(false);
    await handleManualSave();
    navigate(`/my-scripts?tab=edit&template=${templateId}`);
  }, [handleManualSave, navigate, templateId]);

  // Leave without saving (from save-before-leave confirm dialog)
  const confirmLeaveWithoutSaving = useCallback(() => {
    setShowSaveBeforeLeaveConfirm(false);
    navigate(`/my-scripts?tab=edit&template=${templateId}`);
  }, [navigate, templateId]);
  
  // Handle "Configure Details" - navigate to Script Editor for advanced settings
  const handleConfigureDetails = useCallback(async (nodeId) => {
    console.log('🔧 Configure Details clicked for node:', nodeId);
    
    // Save changes first
    if (hasUnsavedChanges && !isReadOnly) {
      await handleManualSave();
    }
    
    // Navigate to Script Editor with the template selected
    // The node ID is passed for future enhancement (auto-scroll to node)
    navigate(`/my-scripts?tab=edit&template=${templateId}&node=${nodeId}`);
  }, [hasUnsavedChanges, isReadOnly, handleManualSave, navigate, templateId]);
    
  // Warn before closing browser with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && !isReadOnly) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return ''; // Some browsers show this message
      }
    };
        
    window.addEventListener('beforeunload', handleBeforeUnload);
        
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, isReadOnly]);
    
  // Permission check - admins/team leads can view all templates, but only edit non-read-only ones
  const canView = isAdmin || isTeamLead;
  const canEdit = canView && !isReadOnly;
    
  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading flowchart...</p>
        </div>
      </div>
    );
  }
    
  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Template</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={handleBack}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
                        Back to Script Editor
          </button>
        </div>
      </div>
    );
  }
    
  if (!canView) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="text-yellow-600 text-5xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">You don&apos;t have permission to view this template.</p>
          <button
            onClick={handleBack}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
                        Back to Script Editor
          </button>
        </div>
      </div>
    );
  }
    
  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="md"
              onClick={handleBack}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              }
              title="Back to Script Editor"
            >
              Back
            </Button>
                        
            <div className="border-l border-gray-300 h-8"></div>
                        
            <div>
              <Breadcrumbs items={[
                { label: 'Home', href: '/call-script' },
                { label: 'Script Builder', href: `/my-scripts?tab=edit&template=${templateId}` },
                { label: 'Flowchart Editor', current: true }
              ]} />
              <h1 className="text-xl font-semibold text-gray-800">{templateName}</h1>
            </div>
          </div>
                    
          <div className="flex items-center gap-4">
            {/* Lead form editing is now done on the Leads page */}

            {/* Unsaved Changes Indicator */}
            {hasUnsavedChanges && !isReadOnly && (
              <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-2 rounded-lg border border-amber-200">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">Unsaved Changes</span>
              </div>
            )}
                        
            {/* Save Status */}
            {saveStatus && (
              <div className={`text-sm px-3 py-2 rounded-lg font-medium ${
                saveStatus.includes('saved') ? 'bg-green-100 text-green-700 border border-green-200' :
                  saveStatus.includes('Saving') ? 'bg-blue-100 text-blue-700 border border-blue-200' :
                    saveStatus.includes('discarded') ? 'bg-amber-100 text-amber-700 border border-amber-200' :
                      'bg-red-100 text-red-700 border border-red-200'
              }`}>
                {isSaving && (
                  <span className="inline-block mr-2">
                    <div className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                  </span>
                )}
                {saveStatus}
              </div>
            )}
                        
            {/* Action Buttons */}
            {!isReadOnly && (
              <div className="flex items-center gap-2">
                {/* Discard Changes Button */}
                <Button
                  variant="secondary"
                  size="md"
                  onClick={handleDiscardChanges}
                  disabled={!hasUnsavedChanges || isSaving}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  }
                  title="Discard all unsaved changes"
                >
                  Discard Changes
                </Button>
                                
                {/* Save Changes Button */}
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleManualSave}
                  disabled={!hasUnsavedChanges || isSaving}
                  loading={isSaving}
                  icon={
                    !isSaving ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg> : undefined
                  }
                  title="Save all changes"
                >
                  Save Changes
                </Button>
              </div>
            )}
                        
            {isReadOnly && (
              <span className="text-sm text-gray-500 italic px-3 py-2 bg-gray-100 rounded-lg">Read-only</span>
            )}
          </div>
        </div>
      </div>
            
      {/* FlowChart Editor - Full height minus header */}
      <div className="flex-1 overflow-hidden">
        {graph && (
          <FlowChartEditor
            key={`flowchart-${templateId}-${refreshKey}`}
            ref={editorRef}
            graph={graph}
            steps={templateMetadata?.steps || []}
            onChange={handleGraphChange}
            onConfigureDetails={handleConfigureDetails}
            readOnly={isReadOnly}
            viewportPersistKey={`flowViewport:${templateId}`}
            initialNodeId={initialNodeId}
          />
        )}
      </div>

      {/* Lead form editing is now done on the Leads page */}

      {/* Discard Changes Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        onConfirm={confirmDiscardChanges}
        title="Discard Changes?"
        message="Are you sure you want to discard all unsaved changes? This cannot be undone."
        confirmLabel="Discard"
        confirmVariant="danger"
      />

      {/* Save Before Leave Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showSaveBeforeLeaveConfirm}
        onClose={confirmLeaveWithoutSaving}
        onConfirm={confirmSaveAndLeave}
        title="Unsaved Changes"
        message="You have unsaved changes. Would you like to save before leaving?"
        confirmLabel="Save & Leave"
        cancelLabel="Leave Without Saving"
        confirmVariant="primary"
      />
    </div>
  );
};

export default FlowChartEditorPage;

